import { createApi } from '@reduxjs/toolkit/query/react';
import { config } from '../../constants/config';
import { createBaseQuery } from './baseQuery';

export interface ManagementClient {
  clientId: string;
  organizationName: string;
  organizationId: string;
  scopes: string[];
  created: string;
  businessSystem: string;
  clientIPs: string[];
}

export interface CreateManagementClientRequest {
  clientId: string;
  organizationName: string;
  organizationId: string;
  businessSystem?: string;
}

export interface UpdateManagementClientRequest {
  clientId: string;
  scope?: string;
  clientIPs?: string[];
}

export interface CycleSecretRequest {
  clientId: string;
}

export interface CycleSecretResponse {
  clientId: string;
  secret: string;
}

export interface PagedResult<T> {
  items: T[];
  totalCount: number;
  page: number;
  pageSize: number;
  totalPages: number;
  hasNextPage: boolean;
  hasPreviousPage: boolean;
}

export const managementClientsApi = createApi({
  reducerPath: 'managementClientsApi',
  baseQuery: createBaseQuery(`${config.apiUrl}/api/admin`),
  tagTypes: ['ManagementClient'],
  endpoints: (builder) => ({
    getManagementClients: builder.query<PagedResult<ManagementClient>, {
      page?: number;
      pageSize?: number;
      search?: string;
      organizationId?: string;
      businessSystem?: string;
    }>({
      query: (params) => ({
        url: '/management-clients',
        params: {
          page: params.page || 1,
          pageSize: params.pageSize || 20,
          ...(params.search && { search: params.search }),
          ...(params.organizationId && { organizationId: params.organizationId }),
          ...(params.businessSystem && { businessSystem: params.businessSystem }),
        },
      }),
      providesTags: ['ManagementClient'],
    }),
    
    getManagementClient: builder.query<ManagementClient, string>({
      query: (id) => `/client/${id}`,
      transformResponse: (response: { client: ManagementClient }) => response.client,
      providesTags: (result, error, id) => [{ type: 'ManagementClient', id }],
    }),
    
    createManagementClient: builder.mutation<any, CreateManagementClientRequest>({
      query: (client) => ({
        url: '/client',
        method: 'POST',
        body: client,
      }),
      invalidatesTags: ['ManagementClient'],
    }),
    
    updateManagementClient: builder.mutation<any, UpdateManagementClientRequest>({
      query: (client) => ({
        url: '/client/update',
        method: 'POST',
        body: client,
      }),
      invalidatesTags: ['ManagementClient'],
    }),
    
    cycleClientSecret: builder.mutation<CycleSecretResponse, CycleSecretRequest>({
      query: (request) => ({
        url: '/client/cycleSecret',
        method: 'POST',
        body: request,
      }),
      invalidatesTags: ['ManagementClient'],
    }),
    
    deleteManagementClient: builder.mutation<any, string>({
      query: (id) => ({
        url: `/client/${id}`,
        method: 'DELETE',
      }),
      invalidatesTags: ['ManagementClient'],
    }),
  }),
});

export const {
  useGetManagementClientsQuery,
  useGetManagementClientQuery,
  useCreateManagementClientMutation,
  useUpdateManagementClientMutation,
  useCycleClientSecretMutation,
  useDeleteManagementClientMutation,
} = managementClientsApi;